import React, { useCallback, useEffect, useState } from "react";
import useToken from "../../../../hooks/auth/useToken";
import {
  interacEmailService,
  InteracEmailEvent,
} from "../../services/interacEmailService";

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    credited: "bg-emerald-100 text-emerald-800",
    shortfall: "bg-amber-100 text-amber-800",
    processed_ok: "bg-sky-100 text-sky-800",
    processed_error: "bg-red-100 text-red-800",
    incomplete: "bg-orange-100 text-orange-800",
  };
  return map[status] || "bg-gray-100 text-gray-700";
};

const InteracEmailsPage: React.FC = () => {
  const { token } = useToken();
  const [items, setItems] = useState<InteracEmailEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await interacEmailService.list(token, {
        page,
        pageSize: 25,
        status: status || undefined,
        q: q || undefined,
      });
      setItems(data.items || []);
      setTotalPages(data.pagination?.total_pages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [token, page, status, q]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Emails Interac</h1>
        <p className="text-sm text-gray-500 mt-1">
          Historique de chaque e-Transfer détecté : référence présente ou non, shortfall,
          crédit, payout. Source : table <code>interac_processed_emails</code>.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Statut</label>
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Tous</option>
            <option value="credited">credited</option>
            <option value="shortfall">shortfall</option>
            <option value="processed_ok">processed_ok</option>
            <option value="processed_error">processed_error</option>
            <option value="incomplete">incomplete</option>
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Recherche</label>
          <input
            className="w-full border rounded-md px-3 py-2 text-sm"
            placeholder="email, référence, résumé…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                load();
              }
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setPage(1);
            load();
          }}
          className="px-4 py-2 rounded-md bg-slate-800 text-white text-sm"
        >
          Filtrer
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-3">{error}</div>}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Montant</th>
                <th className="px-4 py-3">Réf.</th>
                <th className="px-4 py-3">Avec ref?</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Match</th>
                <th className="px-4 py-3">Résumé / description</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    Chargement…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    Aucun événement
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row) => (
                  <tr key={row.id} className="border-t align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {row.created_at ? new Date(row.created_at).toLocaleString("fr-CA") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusBadge(row.status)}`}>
                        {row.status}
                      </span>
                      {row.reason && (
                        <div className="text-xs text-gray-400 mt-1">{row.reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        reçu: <strong>{row.received_amount ?? row.amount ?? "—"}</strong>
                      </div>
                      {row.expected_amount != null && (
                        <div className="text-xs text-gray-500">attendu: {row.expected_amount}</div>
                      )}
                      {row.missing_amount != null && row.missing_amount > 0 && (
                        <div className="text-xs text-amber-700">manque: {row.missing_amount}</div>
                      )}
                      {row.credited_amount != null && (
                        <div className="text-xs text-emerald-700">crédité: {row.credited_amount}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{row.reference || "—"}</td>
                    <td className="px-4 py-3">
                      {row.has_reference === true ? (
                        <span className="text-emerald-700">oui</span>
                      ) : row.has_reference === false ? (
                        <span className="text-amber-700 font-medium">non</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{row.reply_to || "—"}</div>
                      <div className="text-gray-400 truncate max-w-[160px]">{row.sender}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{row.match_method || "—"}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 max-w-md">
                      <div className="whitespace-pre-wrap break-words">{row.summary || "—"}</div>
                      {row.error_message && (
                        <div className="text-red-600 mt-1">{row.error_message}</div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-600">
          <span>
            {total} événement{total > 1 ? "s" : ""}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Préc.
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Suiv.
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteracEmailsPage;
